import axios from 'axios'
import memoizee from 'memoizee'
import axiosRetry, { isNetworkError } from 'axios-retry'
import { deserializeJsonApi } from '@/utils/jsonApiDeserializer.js'
import { logErrorOnSentry } from '@/utils/errorReportingUtils.js'
import { tap, get } from 'lodash'
import { metaTagContent } from '@/helpers/documentHelpers.js'
import { removeTrailingSlash } from '@/utils/stringUtils.js'

axios.defaults.headers.common['X-CSRF-Token'] = metaTagContent('csrf-token')

const api = axios.create()

export const apiWithRetry = tap(axios.create(), _api =>
  axiosRetry(_api, {
    retries: 3,
    retryCondition: e => isNetworkError(e),
    onMaxRetryTimesExceeded: (e, retryCount) =>
      logErrorOnSentry(e, { component: 'api', function: 'apiWithRetry' }, { retryCount: retryCount }),
  }),
)

export const objectToFormData = data => {
  const formData = new FormData()
  Object.keys(data).forEach(key => {
    const value = data[key]
    if (typeof value === 'object') {
      Object.keys(value).forEach(subKey => {
        const subValue = value[subKey]
        formData.append(`${key}[${subKey}]`, subValue)
      })
    } else {
      formData.append(key, value)
    }
  })
  return formData
}

export const base64ToBlob = base64 => {
  return fetch(base64).then(res => res.blob())
}

export const base64ToFile = (base64, filename) => {
  return base64ToBlob(base64).then(blob => {
    return new File([blob], filename)
  })
}

export const fileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })

export const apiGet = async (...args) => {
  const { data } = await api.get(...args)
  return deserializeJsonApi(data)
}

export const apiGetCached = memoizee(endpoint => api.get(endpoint), { promise: true })

export const apiGetJsonCached = async (endpoint, key = 'data') => {
  const response = await apiGetCached(endpoint)
  const data = get(response, key)
  return deserializeJsonApi(data)
}

// We need to reset CSRF Token in case we sign in with XHR
// The backend SHOULD respond X-New-CSRF-Token
export const createApi = (csrfToken = null) => {
  axios.defaults.headers.common['X-CSRF-Token'] = csrfToken ?? metaTagContent('csrf-token')
  return axios.create() // Reset global variable
}

export const handleHttpStatus = (statuses, handler = () => {}) => {
  return err => {
    if (statuses.includes(err.response?.status)) {
      handler(err)
    } else throw err
  }
}

export const getCurrentPath = () => removeTrailingSlash(location.pathname)

export default api
